import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import './AdminProducts.css';

const AdminProducts = () => {
  usePageTitle('Manage Products');
  const navigate = useNavigate();
  const { isFounder } = useAuth();
  const { success, error: showError } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'medal',
    price: '',
    stock: '',
    is_available: true,
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'medal', label: 'Medals', icon: '🏅' },
    { value: 'sticker', label: 'Stickers', icon: '🎨' },
    { value: 'hoodie', label: 'Hoodies', icon: '🧥' },
    { value: 't_shirt', label: 'T-Shirts', icon: '👕' },
    { value: 'cap', label: 'Caps', icon: '🧢' },
    { value: 'mug', label: 'Mugs', icon: '☕' },
  ];

  useEffect(() => {
    if (!isFounder) {
      navigate('/admin');
      return;
    }
    fetchProducts();
  }, [isFounder, navigate, selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? { category: selectedCategory } : {};
      const response = await api.get('/products', { params });
      const productsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data) 
          ? response.data.data 
          : []);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Error fetching products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'medal',
        price: product.price || '',
        stock: product.stock !== null && product.stock !== undefined ? product.stock : '',
        is_available: product.is_available !== undefined ? product.is_available : true,
        image: null,
      });
      setImagePreview(product.image_url || product.image ? (product.image_url ? `${product.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${product.image}?t=${Date.now()}`) : null);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        category: 'medal',
        price: '',
        stock: '',
        is_available: true,
        image: null,
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('category', formData.category);
      submitData.append('price', formData.price);
      submitData.append('stock', formData.stock || '');
      submitData.append('is_available', formData.is_available ? '1' : '0');
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Product updated successfully!');
      } else {
        await api.post('/products', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Product created successfully!');
      }

      setShowModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      showError(error.response?.data?.message || 'Error saving product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await api.delete(`/products/${productId}`);
      success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      showError(error.response?.data?.message || 'Error deleting product');
    }
  };

  const toggleAvailability = async (product) => {
    try {
      const submitData = new FormData();
      submitData.append('name', product.name);
      submitData.append('description', product.description || '');
      submitData.append('category', product.category);
      submitData.append('price', product.price);
      submitData.append('stock', product.stock !== null && product.stock !== undefined ? product.stock : '');
      submitData.append('is_available', (!product.is_available).toString());

      await api.put(`/products/${product.id}`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      success(`Product ${!product.is_available ? 'activated' : 'deactivated'} successfully!`);
      fetchProducts();
    } catch (error) {
      console.error('Error toggling availability:', error);
      showError('Error updating product availability');
    }
  };

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="admin-products-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="admin-products-container">
      <div className="admin-products-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Dashboard
        </button>
        <h1>🛍️ Manage Products</h1>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          ➕ Add New Product
        </button>
      </div>

      <div className="category-filters">
        <button
          className={`category-filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('all')}
        >
          All ({products.length})
        </button>
        {categories.map((cat) => {
          const count = products.filter(p => p.category === cat.value).length;
          return (
            <button
              key={cat.value}
              className={`category-filter-btn ${selectedCategory === cat.value ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.value)}
            >
              {cat.icon} {cat.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="products-table-container">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <p>No products found. Create your first product!</p>
          </div>
        ) : (
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="product-image-cell">
                      {product.image_url || product.image ? (
                        <img
                          src={product.image_url ? `${product.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${product.image}?t=${Date.now()}`}
                          alt={product.name}
                          className="product-thumbnail"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50x50/0a0e27/00ff88?text=Product';
                          }}
                        />
                      ) : (
                        <div className="product-placeholder-small">🛍️</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="product-name-cell">
                      <strong>{product.name}</strong>
                      {product.description && (
                        <span className="product-description-small">
                          {product.description.substring(0, 50)}
                          {product.description.length > 50 ? '...' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">
                      {categories.find(c => c.value === product.category)?.icon} {product.category.replace('_', ' ')}
                    </span>
                  </td>
                  <td>${product.price || 0}</td>
                  <td>
                    {product.stock !== null && product.stock !== undefined 
                      ? product.stock 
                      : '∞'}
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${product.is_available ? 'active' : 'inactive'}`}
                      onClick={() => toggleAvailability(product)}
                    >
                      {product.is_available ? '✅ Available' : '❌ Unavailable'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleOpenModal(product)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(product.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Stock (leave empty for unlimited)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_available}
                    onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                  />
                  Available for purchase
                </label>
              </div>
              <div className="form-group">
                <label>Product Image</label>
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;

